import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  FLEX_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { FLEX_START } from '../../../utils/spds/constants/css-layout';
import { hash } from '@ember/helper';
import { isEmpty } from '@ember/utils';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import ContentContainer from './accordion/content-container.gjs';
import FlexColumn from '../layout/flex/column.gjs';
import Super from '../core/base.gjs';
import Trigger from './accordion/trigger.gjs';
import styles from './accordion.module.scss';

const METADATA = Metadata.define({
  description: 'Interactive heading with a collapsible content area',
  extends: [
    pick(ACTION_ARGS, ['onClose', 'onOpen']),
    pick(COMPONENT_STATE_ARGS, ['disabled', 'open', { name: 'animated', default: true }]),
    pick(CONTAINER_ARGS, ['reverse']),
    pick(CONTENT_ARGS, [
      { name: 'icon', default: 'dropdown-caret', description: 'Icon to display in trigger' },
    ]),
    pick(FLEX_ARGS, ['flex']),
  ],
  meta: {
    contentColumn: Arg.boolean({ default: true, description: 'Display content in column layout' }),
    contentGrid: Arg.boolean({ description: 'Display content in grid layout' }),
    triggerGap: Arg.string({
      default: '12px',
      description: 'Gap between icon and text in trigger',
    }),
    triggerJustifyContent: Arg.string({
      default: FLEX_START,
      description: 'Justify content alignment in trigger',
    }),
  },
  name: 'Accordion',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=18965-95563&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/59ce8f-accordion',
  },
});

export default class SpdsContainerAccordion extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-container-accordion';
  classesToMap = ['open'];
  styles = styles;

  @tracked _open;

  get animated() {
    return this.argOrDefault('animated');
  }

  get contentContainerId() {
    return this.composeSubcomponentId('content-container');
  }

  get open() {
    return this._open ?? this.constructor.metadata.getDefault('open');
  }

  set open(value) {
    this._open = value;
  }

  get triggerId() {
    return this.composeSubcomponentId('trigger');
  }

  toggleOpenFromExternalSource = modifier(() => {
    let { open } = this.args;

    if (isEmpty(open)) return;

    this.open = open;
  });

  toggleOpen = () => {
    this.open = !this.open;

    this.open ? this.args.onOpen?.() : this.args.onClose?.();
  };

  <template>
    <FlexColumn
      @flex={{@flex}}
      class={{this.classes}}
      data-test-spds-container-accordion
      {{this.setupComponent}}
      {{this.toggleOpenFromExternalSource}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield
        (hash
          Trigger=(component
            Trigger
            contentContainerId=this.contentContainerId
            disabled=@disabled
            gap=@triggerGap
            icon=@icon
            id=this.triggerId
            justifyContent=@triggerJustifyContent
            onClick=this.toggleOpen
            open=this.open
          )
          ContentContainer=(component
            ContentContainer
            animated=this.animated
            column=@contentColumn
            grid=@contentGrid
            id=this.contentContainerId
            open=this.open
            reverse=@reverse
            triggerId=this.triggerId
          )
        )
      }}
    </FlexColumn>
  </template>
}

export const ACCORDION_METADATA = SpdsContainerAccordion.metadata;
